"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
/*
 * Copyright 2023, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
const react_1 = __importDefault(require("react"));
const AdminMonitoringPage_1 = __importDefault(require("../pages/AdminMonitoringPage"));
const ReviewPage_1 = __importDefault(require("../pages/ReviewPage"));
const SurveyCorrection_1 = __importDefault(require("../pages/SurveyCorrection"));
const RespondentBehaviorPage_1 = __importDefault(require("../pages/RespondentBehaviorPage"));
const SurveyUnavailablePage_1 = __importDefault(require("../../pages/SurveyUnavailablePage"));
const NotFoundPage_1 = __importDefault(require("../../pages/NotFoundPage"));
const UnauthorizedPage_1 = __importDefault(require("chaire-lib-frontend/lib/components/pages/UnauthorizedPage"));
const MaintenancePage_1 = __importDefault(require("chaire-lib-frontend/lib/components/pages/MaintenancePage"));
const pages_1 = require("chaire-lib-frontend/lib/components/pages");
const RegisterPage_1 = __importDefault(require("chaire-lib-frontend/lib/components/pages/RegisterPage"));
const ForgotPasswordPage_1 = __importDefault(require("chaire-lib-frontend/lib/components/pages/ForgotPasswordPage"));
const VerifyPage_1 = __importDefault(require("chaire-lib-frontend/lib/components/pages/VerifyPage"));
const SurveyWithErrorBoundary_1 = __importDefault(require("../../hoc/SurveyWithErrorBoundary"));
const PrivateRoute_1 = __importDefault(require("chaire-lib-frontend/lib/components/routers/PrivateRoute"));
const ResetPasswordPage_1 = __importDefault(require("chaire-lib-frontend/lib/components/pages/ResetPasswordPage"));
const UnconfirmedPage_1 = __importDefault(require("chaire-lib-frontend/lib/components/pages/UnconfirmedPage"));
const PublicRoute_1 = __importDefault(require("chaire-lib-frontend/lib/components/routers/PublicRoute"));
const AdminRoute_1 = __importDefault(require("chaire-lib-frontend/lib/components/routers/AdminRoute"));
const project_config_1 = __importDefault(require("chaire-lib-common/lib/config/shared/project.config"));
const UsersPage_1 = __importDefault(require("chaire-lib-frontend/lib/components/pages/admin/UsersPage"));
const InterviewsByAccessCode_1 = __importDefault(require("../interviews/InterviewsByAccessCode"));
const InterviewsPage_1 = __importDefault(require("../pages/InterviewsPage"));
const Auth_1 = require("chaire-lib-frontend/lib/actions/Auth");
const AdminHomePage_1 = __importDefault(require("../pages/AdminHomePage"));
const AdminRootLayout_1 = __importDefault(require("./AdminRootLayout"));
// Only show user info for users that are not simple respondents
(0, Auth_1.setShowUserInfoPerm)({ Interviews: ['read', 'update'] });
// FIXME This should be done at another level, using the `setProjectConfiguration`
project_config_1.default.auth = {
    localLogin: {
        allowRegistration: true,
        forgotPasswordPage: true,
        registerWithEmailOnly: true,
        confirmEmail: true,
        confirmEmailStrategy: 'confirmByAdmin'
    }
};
/**
 * Route configuration for admin survey application using React Router Data mode.
 * This function returns the routes to ensure components are properly instantiated.
 */
const getAdminSurveyRoutes = () => [
    {
        element: react_1.default.createElement(AdminRootLayout_1.default, null),
        children: [
            {
                path: '/',
                element: react_1.default.createElement(PublicRoute_1.default, { component: pages_1.LoginPage, path: "/" })
            },
            {
                path: '/login',
                element: react_1.default.createElement(PublicRoute_1.default, { component: pages_1.LoginPage, path: "/login" })
            },
            {
                path: '/register',
                element: react_1.default.createElement(PublicRoute_1.default, { component: RegisterPage_1.default, path: "/register" })
            },
            {
                path: '/forgot',
                element: react_1.default.createElement(PublicRoute_1.default, { component: ForgotPasswordPage_1.default, path: "/forgot" })
            },
            {
                path: '/unconfirmed',
                element: react_1.default.createElement(PublicRoute_1.default, { component: UnconfirmedPage_1.default, path: "/unconfirmed" })
            },
            {
                path: '/verify/:token',
                element: react_1.default.createElement(AdminRoute_1.default, { component: VerifyPage_1.default, path: "/verify/:token" })
            },
            {
                path: '/reset/:token',
                element: react_1.default.createElement(PublicRoute_1.default, { component: ResetPasswordPage_1.default, path: "/reset/:token" })
            },
            {
                path: '/unauthorized',
                element: react_1.default.createElement(PublicRoute_1.default, { component: UnauthorizedPage_1.default, path: "/unauthorized" })
            },
            {
                path: '/maintenance',
                element: (react_1.default.createElement(PublicRoute_1.default, { component: MaintenancePage_1.default, componentProps: { linkPath: '/survey' }, path: "/maintenance" }))
            },
            {
                path: '/survey/edit/:uuid',
                element: (react_1.default.createElement(PrivateRoute_1.default, { component: SurveyWithErrorBoundary_1.default, permissions: { Interviews: ['read', 'update'] }, path: "/survey/edit/:uuid" }))
            },
            {
                path: '/survey/edit/:uuid/:sectionShortname',
                element: (react_1.default.createElement(PrivateRoute_1.default, { component: SurveyWithErrorBoundary_1.default, permissions: { Interviews: ['read', 'update'] }, path: "/survey/edit/:uuid/:sectionShortname" }))
            },
            {
                path: '/admin/survey/:sectionShortname',
                element: (react_1.default.createElement(PrivateRoute_1.default, { component: SurveyCorrection_1.default, permissions: { Interviews: ['validate'] }, path: "/admin/survey/:sectionShortname" }))
            },
            {
                path: '/admin/survey/interview/:interviewUuid',
                element: (react_1.default.createElement(PrivateRoute_1.default, { component: SurveyCorrection_1.default, permissions: { Interviews: ['validate'] }, path: "/admin/survey/interview/:interviewUuid" }))
            },
            {
                path: '/interviews/byCode/:accessCode',
                element: (react_1.default.createElement(PrivateRoute_1.default, { component: InterviewsByAccessCode_1.default, permissions: { Interviews: ['read', 'update'] }, path: "/interviews/byCode/:accessCode" }))
            },
            {
                path: '/interviews/byCode',
                element: (react_1.default.createElement(PrivateRoute_1.default, { component: InterviewsByAccessCode_1.default, permissions: { Interviews: ['read', 'update'] }, path: "/interviews/byCode" }))
            },
            {
                path: '/interviews',
                element: (react_1.default.createElement(PrivateRoute_1.default, { component: InterviewsPage_1.default, permissions: { Interviews: ['read', 'update'] }, path: "/interviews" }))
            },
            {
                path: '/admin/monitoring',
                element: react_1.default.createElement(AdminRoute_1.default, { component: AdminMonitoringPage_1.default, path: "/admin/monitoring" })
            },
            {
                path: '/admin/respondent-behavior',
                element: react_1.default.createElement(AdminRoute_1.default, { component: RespondentBehaviorPage_1.default, path: "/admin/respondent-behavior" })
            },
            {
                path: '/admin/validation',
                element: (react_1.default.createElement(PrivateRoute_1.default, { component: ReviewPage_1.default, permissions: { Interviews: ['validate'] }, path: "/admin/validation" }))
            },
            {
                path: '/admin/users',
                element: react_1.default.createElement(AdminRoute_1.default, { component: UsersPage_1.default, path: "/admin/users" })
            },
            {
                path: '/admin',
                element: react_1.default.createElement(AdminRoute_1.default, { component: AdminMonitoringPage_1.default, path: "/admin" })
            },
            {
                path: '/home',
                element: react_1.default.createElement(PrivateRoute_1.default, { component: AdminHomePage_1.default, path: "/home" })
            },
            {
                path: '/unavailable',
                element: react_1.default.createElement(PrivateRoute_1.default, { component: SurveyUnavailablePage_1.default, path: "/unavailable" })
            },
            {
                path: '/*',
                element: react_1.default.createElement(PublicRoute_1.default, { component: NotFoundPage_1.default, path: "/*" })
            }
        ]
    }
];
exports.default = getAdminSurveyRoutes;
//# sourceMappingURL=AdminSurveyRouter.js.map